import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, ConfirmDialog } from '../../components/ui';
import {
  listFiles, smartUpload, openFileInNewTab, downloadFile, deleteFile,
} from '../../api/filesApi';
import { formatDateUTC } from '../../utils/dates';
import { formatBytes } from './formatBytes';

const PAGE_SIZE = 50;
const UPLOAD_THRESHOLD = 100 * 1024 * 1024;
const TABS = [
  { key: 'upload', label: 'Uploads', empty: 'Nothing uploaded yet.' },
  { key: 'output', label: 'Outputs', empty: 'No outputs yet.' },
];

function typeLabel(f) {
  const ext = (f.filename || '').includes('.') ? f.filename.split('.').pop().toUpperCase() : '';
  const subtype = (f.mime_type || '').split('/')[1] || '';
  if (!subtype || subtype.includes('.') || subtype.length > 5) return ext || subtype.toUpperCase();
  return subtype.toUpperCase();
}

function metaLine(f) {
  return [typeLabel(f), formatBytes(f.file_size), formatDateUTC(f.created_at)].filter(Boolean).join(' · ');
}

export function DataPage() {
  const [tab, setTab] = useState('upload');
  const [files, setFiles] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState(false);
  const [query, setQuery] = useState('');
  const [uploading, setUploading] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const requestRef = useRef(0);
  const tabRef = useRef('upload');
  const uploadingRef = useRef(false);
  const inputRef = useRef(null);

  const loadFirst = useCallback((fileType) => {
    const id = ++requestRef.current;
    setStatus('loading');
    listFiles({ limit: PAGE_SIZE, offset: 0, file_type: fileType })
      .then((res) => {
        if (id !== requestRef.current) return;
        setFiles(res.files || []);
        setTotal(res.total || 0);
        setStatus('ready');
      })
      .catch(() => {
        if (id === requestRef.current) setStatus('error');
      });
  }, []);

  useEffect(() => { loadFirst(tab); }, [tab, loadFirst]);

  const switchTab = (key) => {
    if (key === tabRef.current) return;
    tabRef.current = key;
    setQuery('');
    setFiles([]);
    setTotal(0);
    setMoreError(false);
    setTab(key);
  };

  const showMore = async () => {
    const id = requestRef.current;
    setLoadingMore(true);
    setMoreError(false);
    try {
      const res = await listFiles({ limit: PAGE_SIZE, offset: files.length, file_type: tab });
      if (id !== requestRef.current) return;
      setFiles((prev) => [...prev, ...(res.files || [])]);
      setTotal(res.total || 0);
    } catch (e) {
      if (id === requestRef.current) setMoreError(true);
    } finally {
      setLoadingMore(false);
    }
  };

  const onPick = async (e) => {
    const input = e.target;
    const file = input.files && input.files[0];
    if (!file || uploadingRef.current) return;
    uploadingRef.current = true;
    setUploadError(null);
    setUploading(file.name);
    try {
      const created = await smartUpload(file, null, UPLOAD_THRESHOLD);
      if (tabRef.current !== 'upload') {
        // The user moved to another tab mid-upload: land on Uploads and reload.
        tabRef.current = 'upload';
        setQuery('');
        setFiles([]);
        setTotal(0);
        setMoreError(false);
        setTab('upload');
        loadFirst('upload');
        return;
      }
      setFiles((prev) => [created, ...prev.filter((f) => f.file_id !== created.file_id)]);
      setTotal((t) => t + 1);
    } catch (err) {
      setUploadError(`Couldn't upload ${file.name}.`);
    } finally {
      uploadingRef.current = false;
      setUploading(null);
      input.value = '';
    }
  };

  const runAction = async (fn, verb, filename) => {
    setActionError(null);
    try {
      await fn();
    } catch (e) {
      setActionError(`Couldn't ${verb} ${filename}.`);
    }
  };

  const confirmDelete = async () => {
    try {
      await deleteFile(toDelete.file_id);
    } catch (e) {
      setDeleteError("Couldn't delete.");
      throw e;
    }
    setFiles((prev) => prev.filter((f) => f.file_id !== toDelete.file_id));
    setTotal((t) => Math.max(0, t - 1));
    setToDelete(null);
  };

  const needle = query.trim().toLowerCase();
  const visible = needle ? files.filter((f) => (f.filename || '').toLowerCase().includes(needle)) : files;
  const emptyText = TABS.find((t) => t.key === tab).empty;

  return (
    <div className="bg-paper text-ink min-h-full">
      <div className="max-w-6xl mx-auto px-6 py-9 flex flex-col gap-5">
        <div className="flex items-center justify-between gap-4">
          <h1 className="font-display text-4xl font-extrabold tracking-tight">Data</h1>
          <Button variant="primary" disabled={!!uploading} onClick={() => inputRef.current && inputRef.current.click()}>
            Upload
          </Button>
          <input ref={inputRef} type="file" className="hidden" aria-label="Choose a file to upload" onChange={onPick} />
        </div>

        {uploading && <p role="status" className="text-sm text-pencil">{`Uploading ${uploading}…`}</p>}
        {uploadError && <p role="alert" className="text-sm text-status-failed">{uploadError}</p>}
        {actionError && <p role="alert" className="text-sm text-status-failed">{actionError}</p>}

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex gap-2">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                aria-pressed={tab === t.key}
                onClick={() => switchTab(t.key)}
                className={`px-3.5 py-1.5 rounded-full border-1.5 font-semibold text-sm focus-ring ${
                  tab === t.key ? 'border-ink bg-hl-soft text-ink' : 'border-rule text-pencil'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <input
            type="search"
            aria-label="Search by name"
            placeholder="Search by name"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="ml-auto w-64 bg-card border-1.5 border-ink rounded-[10px] px-3 py-1.5 text-sm placeholder:text-pencil focus-ring"
          />
        </div>

        {status === 'loading' && <p className="text-pencil">Loading…</p>}
        {status === 'error' && (
          <div className="flex items-center gap-3">
            <p role="alert">Couldn't load your data.</p>
            <Button variant="secondary" size="sm" onClick={() => loadFirst(tab)}>Try again</Button>
          </div>
        )}
        {status === 'ready' && files.length === 0 && <p className="text-pencil">{emptyText}</p>}

        {status === 'ready' && files.length > 0 && (
          <ul className="flex flex-col gap-2">
            {visible.map((f) => (
              <li key={f.file_id}>
                <Card className="p-3 flex flex-wrap items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold truncate">{f.filename}</div>
                    <div className="text-sm text-pencil">{metaLine(f)}</div>
                    {tab === 'output' && f.workflow_id && (
                      <Link
                        to={`/runs/open?workflow=${encodeURIComponent(f.workflow_id)}&view=visualization`}
                        className="text-sm underline focus-ring"
                      >
                        {f.workflow_name || 'Run'}
                      </Link>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => runAction(() => openFileInNewTab(f.file_id), 'open', f.filename)}>Open</Button>
                    <Button variant="ghost" size="sm" onClick={() => runAction(() => downloadFile(f.file_id, f.filename), 'download', f.filename)}>Download</Button>
                    <Button variant="ghost" size="sm" onClick={() => { setDeleteError(null); setToDelete(f); }}>Delete</Button>
                  </div>
                </Card>
              </li>
            ))}
          </ul>
        )}

        {status === 'ready' && files.length < total && (
          <div className="flex items-center gap-3">
            <Button variant="secondary" disabled={loadingMore} onClick={showMore}>Show more</Button>
            {moreError && <p role="alert" className="text-sm text-status-failed">Couldn't load more.</p>}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!toDelete}
        title={toDelete ? `Delete ${toDelete.filename}?` : ''}
        confirmLabel="Delete"
        error={deleteError}
        onConfirm={confirmDelete}
        onCancel={() => setToDelete(null)}
      />
    </div>
  );
}

export function DataRoute() {
  return <DataPage />;
}

export default DataPage;
